export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  message: string;
  status: "new" | "read" | "replied";
  createdAt: string;
  updatedAt?: string;
}

export interface CreateContactMessageRequest {
  name: string;
  email: string;
  message: string;
}

export interface ContactMessageResponse {
  success: boolean;
  message: string;
  data?: ContactMessage;
}

export interface ContactMessagesResponse {
  success: boolean;
  message?: string;
  data: ContactMessage[];
}

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export const contactService = {
  async createMessage(
    messageData: CreateContactMessageRequest
  ): Promise<ContactMessageResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(messageData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send message");
      }

      return data;
    } catch (error) {
      console.error("Error creating contact message:", error);
      throw error;
    }
  },

  async getAllMessages(): Promise<ContactMessage[]> {
    try {
      // Get Firebase Auth token
      const { auth } = await import("../firebase");
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response:", text);
        throw new Error(
          response.status === 401
            ? "Authentication required. Please sign in."
            : response.status === 403
            ? "Access denied. Admin privileges required."
            : `Server error: ${response.status} ${response.statusText}`
        );
      }

      const data: ContactMessagesResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch messages");
      }

      return data.data;
    } catch (error) {
      console.error("Error fetching contact messages:", error);
      throw error;
    }
  },

  async updateMessageStatus(
    id: string,
    status: "new" | "read" | "replied"
  ): Promise<void> {
    try {
      // Get Firebase Auth token
      const { auth } = await import("../firebase");
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/contact/${id}/status`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response:", text);
        throw new Error(
          response.status === 401
            ? "Authentication required. Please sign in."
            : response.status === 403
            ? "Access denied. Admin privileges required."
            : `Server error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update message status");
      }
    } catch (error) {
      console.error("Error updating message status:", error);
      throw error;
    }
  },

  async deleteMessage(id: string): Promise<void> {
    try {
      // Get Firebase Auth token
      const { auth } = await import("../firebase");
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Authentication required");
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/contact/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response:", text);
        throw new Error(
          response.status === 401
            ? "Authentication required. Please sign in."
            : response.status === 403
            ? "Access denied. Admin privileges required."
            : `Server error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete message");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      throw error;
    }
  },
};
